import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { publicInvoiceAPI } from '@/lib/api';
import { CheckCircle2, FileText, Loader2, CreditCard, AlertTriangle, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function InvoiceView() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [invoice, setInvoice] = useState(null);
  const [error, setError] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);

  useEffect(() => {
    fetchInvoice();
  }, [token]);

  const fetchInvoice = async () => {
    try {
      const response = await publicInvoiceAPI.getInvoice(token);
      setInvoice(response.data);
    } catch (error) {
      setError('Invoice not found or has expired.');
    } finally {
      setLoading(false);
    }
  };

  const handlePayWithStripe = async () => {
    setPaymentLoading(true);
    try {
      // Create Stripe checkout session
      const response = await fetch(`${API_URL}/api/payments/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          invoice_id: invoice.id,
          amount: invoice.amount,
          customer_email: invoice.customer_email || null,
          description: `Invoice ${invoice.invoice_number}`
        })
      });
      
      const data = await response.json();
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        alert('Unable to process payment. Please contact us.');
      }
    } catch (error) {
      alert('Payment error. Please try again or contact us.');
    } finally {
      setPaymentLoading(false);
    }
  };

  const getStatusInfo = (status) => {
    switch (status) {
      case 'paid':
        return { color: 'bg-green-100 text-green-800', icon: CheckCircle2, label: 'PAID' };
      case 'overdue':
        return { color: 'bg-red-100 text-red-800', icon: AlertTriangle, label: 'OVERDUE' };
      default:
        return { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'PENDING' };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-lime-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Invoice Not Found</h2>
            <p className="text-gray-500">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const statusInfo = getStatusInfo(invoice.status);
  const StatusIcon = statusInfo.icon;

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-lime-600">{invoice.company_name}</h1>
          <p className="text-gray-500 mt-2">Invoice #{invoice.invoice_number}</p>
        </div>

        {/* Paid Banner */}
        {invoice.status === 'paid' && (
          <Card className="mb-6 border-green-200 bg-green-50">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <CheckCircle2 className="w-12 h-12 text-green-500" />
                <div>
                  <h3 className="text-xl font-bold text-green-800">Payment Received!</h3>
                  <p className="text-green-600">
                    Thank you! This invoice was paid on {new Date(invoice.paid_at).toLocaleDateString()}.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Overdue Warning */}
        {invoice.status === 'overdue' && (
          <Card className="mb-6 border-red-200 bg-red-50">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <AlertTriangle className="w-12 h-12 text-red-500" />
                <div>
                  <h3 className="text-xl font-bold text-red-800">Payment Overdue</h3>
                  <p className="text-red-600">
                    This invoice was due on {new Date(invoice.due_date).toLocaleDateString()}. Please pay as soon as possible.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Invoice Details */}
        <Card className="mb-6">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Invoice Details</CardTitle>
            <span className={`flex items-center gap-1 px-3 py-1 rounded text-sm font-medium ${statusInfo.color}`}>
              <StatusIcon className="w-4 h-4" />
              {statusInfo.label}
            </span>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Bill To</p>
                <p className="font-semibold text-lg">{invoice.customer_name}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500">Due Date</p>
                <p className="font-semibold">{new Date(invoice.due_date).toLocaleDateString()}</p>
              </div>
            </div>

            {invoice.service_date && (
              <div>
                <p className="text-sm text-gray-500">Service Date</p>
                <p className="font-medium">{new Date(invoice.service_date).toLocaleDateString()}</p>
              </div>
            )}

            {/* Line Items */}
            <div className="border-t pt-4">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 text-sm text-gray-500">Service</th>
                    <th className="text-right py-2 text-sm text-gray-500">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {(invoice.line_items || []).map((item, idx) => (
                    <tr key={idx} className="border-b">
                      <td className="py-3">{item.description}</td>
                      <td className="py-3 text-right">${item.amount?.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td className="py-4 font-bold text-lg">Total</td>
                    <td className="py-4 text-right font-bold text-2xl text-lime-600">
                      ${invoice.amount?.toFixed(2)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {invoice.additional_notes && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-500 mb-1">Notes</p>
                <p className="text-gray-700">{invoice.additional_notes}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Payment Button */}
        {invoice.status !== 'paid' && (
          <Card>
            <CardContent className="p-6">
              <p className="text-center text-gray-600 mb-4">
                Pay securely with credit card
              </p>
              <Button
                className="w-full bg-lime-500 hover:bg-lime-600 text-black text-lg py-6"
                onClick={handlePayWithStripe}
                disabled={paymentLoading}
              >
                {paymentLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5 mr-2" />
                    Pay ${invoice.amount?.toFixed(2)} Now
                  </>
                )}
              </Button>
              <p className="text-center text-xs text-gray-400 mt-4">
                Powered by Stripe. Your payment information is secure.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <p className="text-center text-sm text-gray-400 mt-8">
          Questions about this invoice? Contact {invoice.company_name}
        </p>
      </div>
    </div>
  );
}
